import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, Save, User, Key, Building2, Copy } from 'lucide-react';
import { useUser } from "@/context/user-provider";
import { userService } from "@/services/user-service";
import { apiKeyService } from "@/services/api-key-service";
import { organizationService } from "@/services/organization-service";

export function UserProfile() {
  const {
    user,
    organizations,
    apiKeys,
    refreshApiKeys,
    isOrgLoading,
    isApiKeysLoading
  } = useUser();

  // Profile state - only local UI state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // API Key state - only local UI state
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKeyExpiresIn, setNewApiKeyExpiresIn] = useState<string>('');
  const [newApiKeyPermissions, setNewApiKeyPermissions] = useState<string>('');
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [showGeneratedApiKey, setShowGeneratedApiKey] = useState(false);

  // Initialize profile data from user context
  useEffect(() => {
    if (user) {
      setName(user.name || user.email);
      setEmail(user.email);
    }
  }, [user]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);

    try {
      await userService.updateProfile(name);
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    try {
      await userService.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password. Please check your current password.');
    }
  };

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newApiKeyName.trim()) {
      setError('API Key name is required');
      return;
    }

    try {
      const apiKeyData: any = {
        name: newApiKeyName.trim(),
      };

      if (newApiKeyExpiresIn) {
        apiKeyData.expiresIn = parseInt(newApiKeyExpiresIn) * 1000; // Convert to milliseconds
      }

      if (newApiKeyPermissions) {
        try {
          apiKeyData.permissions = JSON.parse(newApiKeyPermissions);
        } catch (parseError) {
          setError('Permissions must be valid JSON');
          return;
        }
      }

      const result = await apiKeyService.create(apiKeyData);

      // Store the generated API key
      setGeneratedApiKey(result.key || null);
      setNewApiKeyName('');
      setNewApiKeyExpiresIn('');
      setNewApiKeyPermissions('');

      setSuccess('API key created successfully');

      // Refresh API keys from provider
      await refreshApiKeys();
    } catch (err: any) {
      console.error('Error creating API key:', err);
      setError(err.message || 'Failed to create API key');
    }
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    try {
      await apiKeyService.delete(apiKeyId);
      setSuccess('API key deleted successfully');

      // Refresh API keys from provider
      await refreshApiKeys();
    } catch (err: any) {
      console.error('Error deleting API key:', err);
      setError(err.message || 'Failed to delete API key');
    }
  };

  const handleCopyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setSuccess('API key copied to clipboard');
    }).catch(() => {
      setError('Failed to copy API key to clipboard');
    });
  };

  const handleVerifyApiKey = async (apiKey: string) => {
    try {
      const result = await apiKeyService.verify(apiKey);

      if (result.valid) {
        setSuccess('API key is valid and active');
      } else {
        setSuccess('API key is not valid');
      }
    } catch (err: any) {
      console.error('Error verifying API key:', err);
      setError(err.message || 'Failed to verify API key');
    }
  };

  const handleSwitchOrganization = async (orgId: string) => {
    try {
      await organizationService.setActive(orgId);
      setSuccess('Organization switched successfully');
    } catch (err: any) {
      console.error('Error switching organization:', err);
      setError(err.message || 'Failed to switch organization');
    }
  };

  if (!user || isOrgLoading || isApiKeysLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">User Profile</h1>
        <p className="text-muted-foreground">Manage your account settings and preferences</p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="password" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            Password
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="organizations" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Organizations
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    disabled
                  />
                  <p className="text-sm text-muted-foreground">Email cannot be changed</p>
                </div>

                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      Save Changes
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (at least 8 characters)"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmNewPassword"
                      type={showConfirmNewPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    >
                      {showConfirmNewPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <Button type="submit">
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Change Password
                  </span>
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                API Keys
              </CardTitle>
              <CardDescription>Manage your API keys for programmatic access</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Create new API key form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Create New API Key</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateApiKey} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="apiKeyName">API Key Name</Label>
                        <Input
                          id="apiKeyName"
                          value={newApiKeyName}
                          onChange={(e) => setNewApiKeyName(e.target.value)}
                          placeholder="e.g., Production Server, Personal Laptop"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="apiKeyExpiry">Expiry (Optional, in seconds)</Label>
                        <Input
                          id="apiKeyExpiry"
                          type="number"
                          value={newApiKeyExpiresIn}
                          onChange={(e) => setNewApiKeyExpiresIn(e.target.value)}
                          placeholder="e.g., 3600 (1 hour), 2592000 (30 days)"
                        />
                        <p className="text-sm text-muted-foreground">
                          Maximum expiry is 1 year (31536000 seconds)
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="apiKeyPermissions">Permissions (Optional, JSON format)</Label>
                        <Input
                          id="apiKeyPermissions"
                          value={newApiKeyPermissions}
                          onChange={(e) => setNewApiKeyPermissions(e.target.value)}
                          placeholder='e.g., {"files": ["read", "write"], "users": ["read"]}'
                        />
                        <p className="text-sm text-muted-foreground">
                          Define what actions this key can perform
                        </p>
                      </div>

                      <Button type="submit">Create API Key</Button>
                    </form>

                    {generatedApiKey && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-md">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-blue-900">Your New API Key</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Make sure to copy this key now. You won't be able to see it again!
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowGeneratedApiKey(!showGeneratedApiKey)}
                          >
                            {showGeneratedApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <div className="flex-1 p-3 bg-blue-100 rounded-md font-mono text-sm break-all">
                            {showGeneratedApiKey ? generatedApiKey : '••••••••••••••••••••••••••••••••••••••••••••'}
                          </div>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleCopyApiKey(generatedApiKey!)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyApiKey(generatedApiKey!)}
                          >
                            Verify Key
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setGeneratedApiKey(null);
                              setSuccess('Generated key cleared from view');
                            }}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* API Keys list */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Your API Keys</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {apiKeys.length === 0 ? (
                      <p className="text-muted-foreground">No API keys created yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {apiKeys.map((apiKey) => (
                          <div
                            key={apiKey.id}
                            className="flex items-center justify-between p-3 border rounded-md"
                          >
                            <div>
                              <div className="font-medium">
                                {apiKey.name || `API Key ${apiKey.id.slice(0, 8)}...`}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Created: {new Date(apiKey.createdAt).toLocaleDateString()}
                                {apiKey.expiresAt && ` | Expires: ${new Date(apiKey.expiresAt).toLocaleDateString()}`}
                                {apiKey.start && ` | Starts with: ${apiKey.start || ''}`}
                              </div>
                              {apiKey.permissions && (
                                <div className="text-xs mt-1">
                                  <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                                    Permissions: {apiKey.permissions}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              {!apiKey.enabled && (
                                <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                                  Disabled
                                </span>
                              )}
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteApiKey(apiKey.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organizations
              </CardTitle>
              <CardDescription>Manage your organization memberships</CardDescription>
            </CardHeader>
            <CardContent>
              {organizations.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-1">No Organizations</h3>
                  <p className="text-muted-foreground">
                    You are not a member of any organizations yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {organizations.map((org) => (
                    <div
                      key={org.id}
                      className="flex items-center justify-between p-4 border rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <div className="font-medium">{org.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Slug: {org.slug} | Created: {new Date(org.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSwitchOrganization(org.id)}
                      >
                        Switch
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default UserProfile;
